import { Emitter } from 'common/emitter';
import { logger } from 'common/logger';
import { avUtils } from 'common/media';

const VIDEO_CHANGE_DETECTED = 'VIDEO_CHANGE_DETECTED';

/**
 * The default dimensions for the video element and canvas. Lower resolution is
 * okay because we are trying to get general change detection.
 */
const ELEMENT_HEIGHT = 240;
const ELEMENT_WIDTH = 320;

/**
 * Encapsulates detecting when a wired screenshare input has changed its video
 * output, which suggests a device has been connected to it. Generally the
 * wired screenshare input will emit a static frame when in an idle state and
 * that frame changes when a device is connected.
 */
export default class VideoChangeListener extends Emitter {
    /**
     * Initializes a new {@code VideoChangeListener} instance.
     *
     * @param {string} deviceLabel - The label associated with the video input
     * device to be monitored. The label is likely obtained from a call to
     * enumerateDevices.
     * @param {number} initialFrameValue - The rgba sum to use as a baseline
     * to detect when a device may have been plugged in. If not defined it
     * will be calculated using a frame in the input device's media stream.
     */
    constructor(deviceLabel, initialFrameValue) {
        super();

        this._deviceLabel = deviceLabel;
        this._initialFrameValue = initialFrameValue;

        /**
         * The video element is used to play the media stream in order to
         * capture frames.
         *
         * @type {HTMLVideoElement}
         */
        this._videoElement = document.createElement('video');
        this._videoElement.width = ELEMENT_WIDTH;
        this._videoElement.height = ELEMENT_HEIGHT;
        this._videoElement.autoplay = true;

        /**
         * Video frames are drawn onto a canvas and compared to the video frame
         * when idle to detect change.
         *
         * @type {HTMLCanvasElement}
         */
        const canvas = document.createElement('canvas');

        canvas.width = ELEMENT_WIDTH;
        canvas.height = ELEMENT_HEIGHT;

        /**
         * The context of the canvas is used to get statistics about the image
         * drawn onto the canvas, which are then used to detect change.
         *
         * @type {RenderingContext}
         */
        this._context = canvas.getContext('2d');

        /**
         * The WebRTC media stream, wrapped by a JitsiTrack model, which should
         * be monitored for change.
         *
         * @type {JitsiLocalTrack|null}
         */
        this._jitsiLocalTrack = null;

        /**
         * The interval which checks the current video frame with initial idle
         * frame.
         *
         * @type {IntervalID}
         */
        this._diffCheckInterval = null;

        /**
         * It is assumed the listener starts with no device connected and any
         * changes will change this variable to true or back to false.
         *
         * @type {boolean}
         */
        this._isDeviceConnected = false;
    }

    /**
     * Adds a callback to invoke when the video has changed to or from the
     * idle frame.
     *
     * @param {Function} callback - Callback to invoke when there is video
     * change to or from the idle frame.
     * @returns {void}
     */
    addChangeListener(callback) {
        this.addListener(VIDEO_CHANGE_DETECTED, callback);
    }

    /**
     * Cleans up any ongoing processes for listening to video changes.
     *
     * @returns {void}
     */
    destroy() {
        this.removeAllListeners();

        clearInterval(this._diffCheckInterval);

        this._jitsiLocalTrack && this._jitsiLocalTrack.dispose();

        this._videoElement.pause();
        this._videoElement.removeAttribute('src');
        this._videoElement.removeAttribute('srcObject');
        this._videoElement.load();

        logger.log('Video change listener stopped', {
            label: this._deviceLabel
        });
    }

    /**
     * Executes the algorithm to score the current video frame. It returns a value
     * which can be used to compare with results from previous calls to check
     * if a change has occurred in the video. Currently the algorithm uses a sum
     * of all RGB values on the canvas.
     *
     * @private
     * @returns {number}
     */
    getCurrentValue() {
        // First update the canvas so the current video frame can be calculated.
        this._context.drawImage(
            this._videoElement,
            0, 0,
            this._videoElement.videoWidth, this._videoElement.videoHeight,
            0, 0,
            ELEMENT_WIDTH, ELEMENT_HEIGHT);

        // From the context itself get {@type ImageData} from the current image
        // on the canvas. The data attribute is a (very long) array of integers
        // representing the Red, Green, Blue, and then Alpha value for each
        // pixel.
        const { data: rgbaValues }
            = this._context.getImageData(0, 0, ELEMENT_WIDTH, ELEMENT_HEIGHT);

        // The sum of all rgba values.
        let score = 0;

        // Iterate by 4 as the array has RGBA values in order for each pixel.
        for (let i = 0; i < rgbaValues.length; i += 4) {
            // Borrowed/stolen from github.com/lonekorean/diff-cam-scratchpad
            const pixelDiff = (rgbaValues[i] * 0.3)
                + (rgbaValues[i + 1] * 0.6)
                + (rgbaValues[i + 2] * 0.1);

            if (pixelDiff >= 32) {
                score++;
            }
        }

        return score;
    }

    /**
     * Returns whether or not there are any listeners registered to be notified
     * of video changes.
     *
     * @returns {boolean}
     */
    hasActiveListeners() {
        return this.listenerCount(VIDEO_CHANGE_DETECTED) > 0;
    }

    /**
     * De-registers a callback to no longer be notified of when the video stream
     * enters or exits the idle frame.
     *
     * @param {Function} callback - The callback which should no longer be
     * notified.
     * @returns {void}
     */
    removeChangeListener(callback) {
        this.removeListener(VIDEO_CHANGE_DETECTED, callback);
    }

    /**
     * Begins looking for changes from the screensharing input. This method
     * assumes there is nothing connected or that an initial value has been
     * passed in to compare to.
     *
     * @returns {Promise}
     */
    start() {
        if (this._startPromise) {
            return this._startPromise;
        }

        this._startPromise = this._getVideoStream()
            .then(jitsiLocalTrack => {
                this._jitsiLocalTrack = jitsiLocalTrack;
                this._videoElement.srcObject
                    = jitsiLocalTrack.getOriginalStream();

                return new Promise(resolve => {
                    this._videoElement.addEventListener('loadeddata', resolve);
                });
            })
            .then(() => this._startDiffCheckInterval())
            .then(() => {
                logger.log('Video change listener started', {
                    label: this._deviceLabel
                });
            });

        return this._startPromise;
    }

    /**
     * A helper to encapsulate acquiring the WebRTC media stream for the known
     * device label.
     *
     * @private
     * @returns {Promise}
     */
    _getVideoStream() {
        let desiredDeviceId;

        return avUtils.enumerateDevices()
            .then(devices => devices.find(
                device => device.label === this._deviceLabel
                    && device.kind === 'videoinput'))
            .then((deviceInfo = {}) => {
                const { deviceId } = deviceInfo;

                if (!deviceId) {
                    return Promise.reject('Device not found');
                }

                desiredDeviceId = deviceId;

                return avUtils.createLocalVideoTrack(deviceId);
            })
            .then(jitsiLocalTrack => {
                // Verify the correct track has been obtained because jitsi
                // does not use exact device id matching for getUserMedia.

                const mediaStream = jitsiLocalTrack.getOriginalStream();
                const videoTracks = mediaStream && mediaStream.getVideoTracks();
                const videoTrack = videoTracks[0];
                const constraints = videoTrack.getConstraints();

                if (constraints.deviceId !== desiredDeviceId) {
                    jitsiLocalTrack.dispose();

                    return Promise.reject('Did not obtain track');
                }

                return jitsiLocalTrack;
            });
    }

    /**
     * Invokes the callback for video having experienced a significant change
     * from its initial starting point.
     *
     * @param {boolean} isDeviceConnected - Whether or not the change indicates
     * a device has been connected or disconnected.
     * @private
     * @returns {void}
     */
    _notifyChangeDetected(isDeviceConnected) {
        this.emit(VIDEO_CHANGE_DETECTED, isDeviceConnected);
    }

    /**
     * Creates the interval which will periodically check if the video input
     * is displaying something other than the idle frame.
     *
     * @private
     * @returns {void}
     */
    _startDiffCheckInterval() {
        clearInterval(this._diffCheckInterval);

        const rgbScoreAtRest = typeof this._initialFrameValue === 'undefined'
            ? this.getCurrentValue()
            : this._initialFrameValue;

        this._diffCheckInterval = setInterval(() => {
            const currentRGBScore = this.getCurrentValue();

            // The value 1000 is an arbitrary threshold that must be exceed for
            // change to be detected.
            const isDeviceConnected
                = Math.abs(rgbScoreAtRest - currentRGBScore) > 1000;

            if (isDeviceConnected !== this._isDeviceConnected) {
                this._isDeviceConnected = isDeviceConnected;
                logger.log('Video changed detected', {
                    currentScore: currentRGBScore,
                    isDeviceConnected,
                    label: this._deviceLabel,
                    restScore: rgbScoreAtRest
                });
                this._notifyChangeDetected(this._isDeviceConnected);
            }
        }, 1000);
    }
}
